import _                         from 'underscore';
import React, { FC, useContext } from 'react';
import { useFormikContext }      from 'formik';
import { Cell, Grid }            from "styled-css-grid";
import { Divider, List, Popup }  from 'semantic-ui-react';

import { useFeatureFlags, useSemanticTools }                                         from '../../../../hooks';
import { SystemEvent, Uuid }                                                         from '../../../../types';
import { HireTypeDropdown, MultipleInputArrayField, TextField }                      from '../../../components/forms';
import { FinePrintMessage }                                                          from '../../../components/util/messaging/FinePrintMessage';
import { HireTypeItem }                                                              from '../../../components/resources/HireTypeItem';
import { EmptyCollectionMessage }                                                    from '../../../components/util/messaging/EmptyCollectionMessage';
import { LabeledToggleSwitch }                                                       from '../common/visualization/ui-manipulation/ToggleViewControl';
import { ProjectActivityUpdateDialogContext, ProjectActivityUpdateDialogFormValues } from './ProjectActivityUpdateDialog';
import { TextLink }                                                                  from '../../../components/display/TextLink';
import { CorvalLogo, CorvalLogoVersions }                                            from '../../../components/product/CorvalLogo';
import { ProjectContext }                                                            from "@src/views/pages/projects/ProjectIndexPage";
import { DecisionEngineQueries }                                                     from "@src/queries";


export interface ProjectActivityUpdateAreaResourcesDisplayOptions {
    projectId?: Uuid,
    showHireTypes?: boolean,
}

export interface HiresOverrideObject {
    headcount: number,
    hireTypeId: Uuid
}

interface Props extends ProjectActivityUpdateAreaResourcesDisplayOptions {}

const defaultProps: Props = {
    showHireTypes: true,
}

const ProjectActivityUpdateAreaResources: FC<Props> = ( props ) => {

    const { showHireTypes }              = props,
          { projectId }                  = useContext( ProjectContext ),
          { projectActivity, isLoading } = useContext( ProjectActivityUpdateDialogContext ),
          form                           = useFormikContext<ProjectActivityUpdateDialogFormValues>(),
          featureFlags                   = useFeatureFlags(),
          [ semanticTools ]              = useSemanticTools();

    const generateRecommendedSelectionMapping = (): [ recommendedSelectedMap: Map<Uuid, boolean>, anySelected: boolean ] => {

        const selectionMap      = new Map<Uuid, boolean>( [] ),
              { hiresOverride } = form.values,
              hasOverrides      = !_.isEmpty( hiresOverride ),
              anySelected       = [];

        // Map recommended ones to is they are in overrides, and
        // determine if _any_ recommended ones are selected in overrides
        projectActivity.activity.hires.map( hire => {

            const { hireTypeId } = hire,
                  overrideObject = _.findWhere( hiresOverride, { hireTypeId } ) ?? null,
                  isInOverrides  = _.isObject( overrideObject ),
                  isSelected     = ( isInOverrides && hasOverrides );

            selectionMap.set( hire.hireTypeId, isSelected )

            anySelected.push( isSelected );
        } );

        return [ selectionMap, !_.isEmpty( _.compact( anySelected ) ) ];
    }

    const handleEnableHireTypes = ( event: SystemEvent ) => {

        handleNoHireTypesOverrideChanged( event, { checked: true } )

    };

    const handleNoHireTypesOverrideChanged = ( event: SystemEvent, data: { checked: boolean } ) => {

        form.setValues( {
            ...form.values,
            hireTypesApply: data.checked,
            hiresOverride:  [],
        } )
    }

    const handleHireTypesOverrideInserted = ( item: HiresOverrideObject ) => {

        form.setValues( {
            ...form.values,
            hiresOverride: [ ...form.values.hiresOverride, item ],
        } )
    }

    const hireTypesApply                              = form.values.hireTypesApply,
          recommendedHireTypes                        = projectActivity.activity.hires.length ? projectActivity.activity.hires : null,
          [ recommendedSelectedMap, anyRecsSelected ] = generateRecommendedSelectionMapping();

    return (

        <>
            <Grid columns={ 'auto' } rows={ 'auto' } rowGap={ '20px' }>


                { showHireTypes && (
                    <Cell>

                        {/* Display Hire Types List */ }
                        <List relaxed divided>
                            <List.Item>

                                {/* Hire Type Override Selection */ }
                                <MultipleInputArrayField<HiresOverrideObject>
                                    name={ 'hiresOverride' }
                                    label={ 'Hire Type Assignments' }
                                    addLabel={ 'Add Reassignment' }
                                    addDisabled={ isLoading }
                                    removeDisabled={ isLoading }
                                    emptyComponent={
                                        <>
                                            { hireTypesApply
                                              ? <EmptyCollectionMessage
                                                  icon={ 'users' }
                                                  header={ {
                                                      content: (
                                                                   recommendedHireTypes
                                                                   ? <Grid columns={ `auto 1fr` }>
                                                                       <Cell middle><CorvalLogo type={ CorvalLogoVersions.COLOR_HEX_LOGO } width={ 16 } inline/></Cell>
                                                                       <Cell middle>Corval Hire Types in Use</Cell>
                                                                   </Grid>
                                                                   : `About Hire Type Assignments`
                                                               )
                                                  } }
                                                  content={
                                                      <>
                                                          <FinePrintMessage>Activities can be assigned to one or more resources called 'hire types'. You can reassign the default hire types with your own, or unassign them, which will impact hiring needs and budget. { recommendedHireTypes && <>The following hire types and headcounts are recommended for this activity:</> }</FinePrintMessage>

                                                          { recommendedHireTypes && (
                                                              <List className={ 'no-padding' } size={ 'large' } divided relaxed>
                                                                  { recommendedHireTypes.map( ( { hireTypeId, headcount }, index ) => (
                                                                      <List.Item key={ index }>

                                                                          <HireTypeItem as={ 'span' } id={ hireTypeId } attributes={ DecisionEngineQueries.fragments.HIRE_TYPE.QUERY_RETURN( props.projectId ?? projectId ?? undefined ) }>
                                                                              { hireType => (
                                                                                  <Grid columns={ `auto auto 1fr` } columnGap={ '5px' }>
                                                                                      <Cell>{ headcount }</Cell>
                                                                                      <Cell>x</Cell>
                                                                                      <Cell><TextLink className={ 'text--colored-black' } onClick={ () => handleHireTypesOverrideInserted( { hireTypeId, headcount } ) }><strong>{ hireType.name }</strong></TextLink></Cell>
                                                                                  </Grid>
                                                                              ) }
                                                                          </HireTypeItem>

                                                                      </List.Item>
                                                                  ) ) }
                                                              </List>
                                                          ) }

                                                      </>
                                                  }
                                              />
                                              : <EmptyCollectionMessage
                                                  icon={ 'dont' }
                                                  header={ `No Hire Types Used` }
                                                  content={ <FinePrintMessage>This activity is not using any hire types, which means it may incur external professional fees, and will not be considered when scheduling resources. <TextLink onClick={ handleEnableHireTypes }>Enable hire types</TextLink> to let allow hire type assignments to this activity.</FinePrintMessage> }
                                              />
                                            }
                                        </>
                                    }
                                    objectFactory={ () => ( { headcount: 1, hireTypeId: null } ) }
                                    addVisible={ hireTypesApply }
                                    renderField={ ( item, index ) => {

                                        return (
                                            <Grid columns={ `20px 12% 1fr` }>

                                                <Cell middle>

                                                    { recommendedSelectedMap.get( item.hireTypeId ) &&
                                                      <Popup
                                                          content={
                                                              <FinePrintMessage>Recommended by Corval</FinePrintMessage>
                                                          }
                                                          trigger={
                                                              <CorvalLogo type={ CorvalLogoVersions.COLOR_HEX_LOGO } width={ 16 } inline/>
                                                          }/> }

                                                </Cell>
                                                <Cell>

                                                    <TextField
                                                        type='number'
                                                        name={ `hiresOverride[${ index }].headcount` }
                                                        placeholder='Headcount'
                                                        loading={ isLoading }
                                                        disabled={ isLoading }
                                                    />

                                                </Cell>
                                                <Cell>

                                                    <HireTypeDropdown
                                                        fluid
                                                        projectId={ props.projectId ?? projectId ?? undefined }
                                                        name={ `hiresOverride[${ index }].hireTypeId` }
                                                        placeholder={ 'Reassign to...' }
                                                        defaultValue={ null }
                                                        upwards={ true }
                                                        multiple={ false }
                                                        search={ true }
                                                        omit={ _.chain( form.values.hiresOverride )
                                                                .pluck( 'hireTypeId' )
                                                                .without( item.hireTypeId )
                                                                .value() ?? [] }
                                                        selectOnNavigation={ true }
                                                        renderLabel={ semanticTools.blockDropdownRenderLabeling }
                                                        disabled={ isLoading }
                                                    />

                                                </Cell>

                                            </Grid>

                                        )
                                    } }
                                />

                                <Divider/>

                                {/* No Hire Types Toggle */ }
                                <LabeledToggleSwitch
                                    key={ `hireTypesApply-${ hireTypesApply ? 'active' : 'inactive' }` }
                                    label={ 'Use Hire Types' }
                                    onChange={ handleNoHireTypesOverrideChanged }
                                    toggle={ true }
                                    disabled={ isLoading }
                                    defaultChecked={ hireTypesApply }
                                    onLabelClick={ e => handleNoHireTypesOverrideChanged( e, { checked: !hireTypesApply } ) }
                                />

                            </List.Item>
                        </List>

                    </Cell>
                ) }

            </Grid>
        </>
    );
};

ProjectActivityUpdateAreaResources.defaultProps = defaultProps;

export { ProjectActivityUpdateAreaResources }
